import type { SessionMessageEntry } from "@/types";

// #303 (UC-2): das Arbeitsprotokoll eines Schritts lesbar machen.
//
// Der Master liefert ROHE Modell-Nachrichten — für ihn opak, weil sie aus dem SDK kommen.
// Die Gliederung ist deshalb Sache der Oberfläche, und die Signale dafür stehen im Ticket
// („Out-of-the-box-Signale", verifiziert 2026-08-28):
//
//  1. TodoWrite IST die Gliederung. Das Modell pflegt selbst eine Aufgabenliste, jedes Item
//     trägt `content` / `status` / `activeForm` (die „macht gerade"-Zeile im Präsens).
//     Es gibt KEINE Kapitel- oder Zusammenfassungs-Ereignisse im SDK — wer darauf baut,
//     baut auf nichts.
//  2. ⚠ Das Modell entscheidet SELBST, ob es die Liste führt (mehrschrittig ja, trivial
//     oft nein). Ohne Fallback wäre ein trivialer Schritt eine leere Seite — deshalb
//     bekommt jedes Werkzeug eine eigene, kompakte Zeile.
//  3. `thinking`-Blöcke werden NICHT gerendert, nur gezählt. Sie stehen im Protokoll (die
//     Voll-Stufe ist der Leser), aber sie sind seitenlang und würden alles andere begraben.
//
// Alles hier ist PUR und ohne React testbar.

const MAX_LABEL = 160;

export interface TodoEintrag {
  content: string;
  status: "pending" | "in_progress" | "completed";
  /** Präsens-Form („Suche im Web nach …") — die Zeile, die den Abschnitt benennt. */
  activeForm: string;
}

export type ProtokollEintrag =
  | {
      art: "abschnitt";
      /** `activeForm` des laufenden Punkts; `null` = kein Punkt ist in Arbeit. */
      aktiv: string | null;
      erledigt: number;
      gesamt: number;
      todos: TodoEintrag[];
    }
  | {
      art: "werkzeug";
      /** Anzeige-Name (MCP-Werkzeuge als `server:werkzeug`). */
      name: string;
      /** Das aussagekräftige Feld des Aufrufs; `null` = keins bekannt. */
      label: string | null;
    }
  | { art: "text"; text: string };

export interface ProtokollAnsicht {
  eintraege: ProtokollEintrag[];
  /** Wie viele Denk-Schritte übersprungen wurden (nur die Zahl, nie der Inhalt). */
  denkSchritte: number;
  /** Wie viele Nachrichten insgesamt geliefert wurden (auch die nicht darstellbaren). */
  nachrichten: number;
}

/** `mcp__notion__search` → `notion:search`; alles andere unverändert. */
export function werkzeugName(raw: string): string {
  const m = /^mcp__([^_].*?)__(.+)$/.exec(raw);
  return m ? `${m[1]}:${m[2]}` : raw;
}

function kurz(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const t = v.trim();
  if (!t) return null;
  return t.length > MAX_LABEL ? `${t.slice(0, MAX_LABEL)}…` : t;
}

/**
 * Das aussagekräftigste Eingabe-Feld eines Werkzeug-Aufrufs.
 *
 * `Bash.description` und `Agent.description` sind modellgeneriert und menschenlesbar —
 * die Kommandozeile bzw. der Unterauftrag wären es nicht. Unbekannte Werkzeuge bekommen
 * bewusst `null` statt eines geratenen Felds: eine falsche Zeile ist schlechter als keine.
 */
export function werkzeugLabel(raw: string, input: unknown): string | null {
  if (input === null || typeof input !== "object") return null;
  const i = input as Record<string, unknown>;
  switch (raw) {
    case "Bash":
    case "BashOutput":
      return kurz(i.description) ?? kurz(i.command);
    case "WebSearch":
      return kurz(i.query);
    case "WebFetch":
      return kurz(i.url);
    case "Read":
    case "Write":
    case "Edit":
    case "NotebookEdit":
      return kurz(i.file_path);
    case "Agent":
    case "Task":
      return kurz(i.description);
    case "Glob":
    case "Grep":
      return kurz(i.pattern);
    case "Skill":
      return kurz(i.skill);
    default:
      // MCP- und Haus-Werkzeuge (mesh__case …) haben kein einheitliches Feld. Zwei sehr
      // verbreitete Namen werden trotzdem probiert — sonst bleibt es beim Werkzeug-Namen.
      return kurz(i.description) ?? kurz(i.query);
  }
}

function istTodoWrite(name: string): boolean {
  return name === "TodoWrite";
}

/** Liest die Aufgabenliste aus `TodoWrite.input.todos` — defensiv, das ist Fremdformat. */
function leseTodos(input: unknown): TodoEintrag[] {
  if (input === null || typeof input !== "object") return [];
  const roh = (input as { todos?: unknown }).todos;
  if (!Array.isArray(roh)) return [];
  const out: TodoEintrag[] = [];
  for (const t of roh) {
    if (t === null || typeof t !== "object") continue;
    const o = t as Record<string, unknown>;
    const content = typeof o.content === "string" ? o.content : "";
    const activeForm = typeof o.activeForm === "string" ? o.activeForm : content;
    const status =
      o.status === "in_progress" || o.status === "completed" || o.status === "pending"
        ? o.status
        : "pending";
    if (!content && !activeForm) continue;
    out.push({ content, status, activeForm });
  }
  return out;
}

/** Die Content-Blöcke einer Nachricht — `content` kann String ODER Block-Liste sein. */
function bloecke(message: unknown): Array<Record<string, unknown>> {
  if (message === null || typeof message !== "object") return [];
  const content = (message as { content?: unknown }).content;
  if (typeof content === "string") return [{ type: "text", text: content }];
  if (!Array.isArray(content)) return [];
  return content.filter((b): b is Record<string, unknown> => b !== null && typeof b === "object");
}

function rolle(message: unknown): string | null {
  if (message === null || typeof message !== "object") return null;
  const r = (message as { role?: unknown }).role;
  return typeof r === "string" ? r : null;
}

/**
 * Baut die lesbare Gliederung aus den rohen Nachrichten.
 *
 * Gerendert wird ausschliesslich, was der ASSISTENT produziert hat: sein Text, seine
 * Werkzeug-Aufrufe, seine Aufgabenliste. Die `user`-Nachrichten daneben sind der
 * Schritt-Auftrag (steht bereits als „Bekommen" an der Karte) und die Werkzeug-ERGEBNISSE
 * — letztere sind roh, unbegrenzt und tragen nichts zum Verständnis bei; sie würden das
 * Protokoll fluten. Diese Grenze ist eine ANZEIGE-Entscheidung, keine Redaktion: die
 * Route liefert alles, und sie ist ohnehin nur für Inhaber/Admin offen.
 */
export function baueProtokoll(messages: readonly SessionMessageEntry[]): ProtokollAnsicht {
  // Fremdformat-Härtung: die Nachrichten sind für uns opak, also ist auch die HÜLLE eine
  // Annahme. Eine Nicht-Liste darf den Aufklapper nicht fällen.
  if (!Array.isArray(messages)) return { eintraege: [], denkSchritte: 0, nachrichten: 0 };
  const eintraege: ProtokollEintrag[] = [];
  let denkSchritte = 0;
  // Aufeinanderfolgende, identische Listen-Schnappschüsse wären reine Wiederholung —
  // das Modell schreibt die Liste bei jedem Haken komplett neu.
  let letzterAbschnitt = "";

  for (const m of messages) {
    // #303 (Review L3): auch die ELEMENTE sind Fremdformat. Ein `null` in der Liste liess
    // `m.message` werfen und riss den Aufklapper ab — dieselbe Härtung wie in
    // `alsSchrittListe` und `falteDateiVersionen`, hier fehlte sie.
    if (!m || typeof m !== "object") continue;
    if (rolle(m.message) !== "assistant") continue;
    for (const b of bloecke(m.message)) {
      const typ = b.type;
      if (typ === "thinking" || typ === "redacted_thinking") {
        denkSchritte += 1;
        continue;
      }
      if (typ === "text") {
        const text = kurzText(b.text);
        if (text) eintraege.push({ art: "text", text });
        continue;
      }
      if (typ !== "tool_use") continue;

      const roh = typeof b.name === "string" ? b.name : "";
      if (!roh) continue;

      if (istTodoWrite(roh)) {
        const todos = leseTodos(b.input);
        if (todos.length === 0) continue;
        const signatur = todos.map((t) => `${t.status}:${t.content}`).join("|");
        if (signatur === letzterAbschnitt) continue;
        letzterAbschnitt = signatur;
        const laufend = todos.find((t) => t.status === "in_progress");
        eintraege.push({
          art: "abschnitt",
          aktiv: laufend ? laufend.activeForm : null,
          erledigt: todos.filter((t) => t.status === "completed").length,
          gesamt: todos.length,
          todos,
        });
        continue;
      }

      eintraege.push({ art: "werkzeug", name: werkzeugName(roh), label: werkzeugLabel(roh, b.input) });
    }
  }

  return { eintraege, denkSchritte, nachrichten: messages.length };
}

// Fliesstext wird grosszügiger gekappt als ein Werkzeug-Label — er IST die Aussage.
const MAX_TEXT = 4000;
function kurzText(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const t = v.trim();
  if (!t) return null;
  return t.length > MAX_TEXT ? `${t.slice(0, MAX_TEXT)}…` : t;
}

/**
 * Warum es kein Protokoll gibt — im Klartext, nach `grund`.
 *
 * Die Unterscheidung ist der ganze Sinn der vier Werte: „noch nicht dran" und „nichts
 * mitgeschrieben" sehen für den Leser gleich aus, sind aber das Gegenteil voneinander
 * (nichts verloren vs. etwas verloren). Ein unbekannter Wert (neuerer Master) bekommt
 * einen neutralen Satz statt des rohen Codes.
 */
export function protokollGrundText(grund: string | null): string {
  switch (grund) {
    case "noch-nicht-gelaufen":
      return "Dieser Schritt war noch nicht dran — hier fehlt nichts.";
    case "step-laeuft":
      return "Der Schritt arbeitet gerade — das Protokoll entsteht bei seinem Abschluss.";
    case "kein-transcript":
      return "Für diesen Schritt wurde nichts mitgeschrieben (kein Agenten-Turn, oder ein Lauf von vor der Umstellung).";
    case "nicht-verfuegbar":
      return "Das Protokoll ist auf diesem Server nicht abrufbar.";
    default:
      return "Kein Protokoll vorhanden.";
  }
}
